import { AgnosticTheme } from '@awaze/design-system.components.awaze-theme-provider'
import Head from 'next/head'
import { JSX, useContext } from 'react'
import { GlobalProps } from 'src/utils/globalProps'
import { buildSeoCanonical, getAssetUrl } from 'src/utils/helpers/helpers'
import { ThemeContext } from 'styled-components'
import { ISeoMetadataFields } from '../../../@types/generated/contentful-global'
import { LocaleSlug, HrefLangLinks } from '../Utils/HrefLangLinks'

interface CustomHeadProps {
  host?: string
  url: string
  seoData: ISeoMetadataFields
  schemaMarkup?: string
  hrefLangs: LocaleSlug
}

export const CustomHead: React.FC<CustomHeadProps> = ({
  host,
  url,
  seoData,
  schemaMarkup,
  hrefLangs,
}): JSX.Element => {
  const themeContext = useContext(ThemeContext) || AgnosticTheme
  const { brand } = useContext(GlobalProps)

  const stringManifest = {
    name: seoData.seoTitle,
    short_name: brand,
    display: 'standalone',
    orientation: 'portrait',
    description: seoData.seoDescription,
    background_color: themeContext.ColorWhiteOpacity100,
    theme_color: themeContext.ColorThemeDark200,
    icons: [
      {
        src: getAssetUrl(`/brand/${brand}/favicon_x48.png`),
        sizes: '48x48',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_x72.png`),
        sizes: '72x72',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_x96.png`),
        sizes: '96x96',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_x128.png`),
        sizes: '128x128',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_x192.png`),
        sizes: '192x192',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_x384.png`),
        sizes: '384x384',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_x512.png`),
        sizes: '512x512',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_HD.png`),
        sizes: '16x16',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon.svg`),
        sizes: 'any',
        type: 'image/svg+xml',
      },
    ],
  }

  const {
    seoNoindex,
    seoNofollow,
    seoTitle,
    seoCanonical = seoData.seoCanonical ?? buildSeoCanonical(host, url),
    seoDescription,
    seoKeywords,
  } = seoData

  return (
    <Head>
      {seoTitle && <title>{seoTitle}</title>}
      {seoCanonical && <link rel="canonical" href={seoCanonical} />}
      {HrefLangLinks({ hrefLangs, brand })}
      {(seoNoindex || seoNofollow) && (
        <meta
          name="robots"
          content={`${seoNoindex ? 'noindex' : ''} ${
            seoNofollow ? 'nofollow' : ''
          }`.trim()}
        />
      )}
      <link
        rel="manifest"
        href={`data:application/json;charset=utf-8,${JSON.stringify(
          stringManifest,
        )}`}
      />
      <link
        rel="apple-touch-icon"
        sizes="192x192"
        href={getAssetUrl(`/brand/${brand}/favicon_x192.png`)}
      />
      <link
        rel="icon"
        type="image/png"
        href={getAssetUrl(`/brand/${brand}/favicon_x48.png`)}
      />
      <link
        rel="icon"
        type="image/svg+xml"
        href={getAssetUrl(`/brand/${brand}/favicon.svg`)}
      />
      <meta
        name="theme-color"
        content={themeContext.ColorThemeBackgroundHeader}
      ></meta>
      {seoDescription && <meta name="description" content={seoDescription} />}
      {seoKeywords?.length > 0 && (
        <meta name="keywords" content={seoKeywords?.join(',')} />
      )}
      {schemaMarkup && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: schemaMarkup }}
        />
      )}
    </Head>
  )
}
